import { useEffect, useMemo, useState } from "react";
import AppCheckbox from "@/ui/components/Checkbox";
import FilterBar from "@/ui/components/FilterBar";
import PageHeader from "@/ui/components/PageHeader";
import ProfitTable from "@/ui/components/ProfitTable";
import Coins from "@/ui/components/Coins";
import { effiencyNumberOfActions } from "@/utils/util";
import MarketplaceTaxInput from "@/ui/components/MarketplaceTaxInput";
import {
  applyMarketplaceTax,
  DEFAULT_MARKETPLACE_TAX_PERCENT,
} from "@/utils/marketplaceTax";
import type {
  CraftingBuffOptions,
  CraftingBuffsMarketData,
} from "@/types/CraftingTypes";

type CraftingEntry = {
  name: string;

  outputResource: number;
  outputItemId: number;

  inputItemId: number;
  inputCount: number;

  actionTimeSeconds: number;
  experienceGain: number;
  requiredLevel: number;
  branchOfInsightDropChance: number;
  outputSellPrice: number;
  inputBuyPrice: number;
};

type BranchOfInsightProps = {
  marketSell: number;
  dropChance: number;
  includeInCalculation: boolean;
};

function computeProfitPer10s(
  entry: CraftingEntry,
  inputBuyPrice: number,
  outputSellPrice: number,
  buffOptions: CraftingBuffOptions,
  branchOfInsight: BranchOfInsightProps,
  marketplaceTaxPercent: number,
): number {
  const totalEfficiencyBonus =
    (buffOptions.useCraftingTea ? 0.02 : 0) +
    (buffOptions.useSuperCraftingTea ? 0.04 : 0) +
    (buffOptions.useUltraCraftingTea ? 0.06 : 0) +
    (buffOptions.useEfficiencyTea ? 0.1 : 0);

  const effectiveActionTimeSeconds =
    entry.actionTimeSeconds / (1 + totalEfficiencyBonus);

  const totalInputBonus = 1 - (buffOptions.useArtisanTea ? 0.1 : 0);
  const totalInputCount = entry.inputCount * totalInputBonus;

  const effectiveOutputCount = 1;

  const outputSellPriceAfterTax = applyMarketplaceTax(
    outputSellPrice,
    marketplaceTaxPercent,
  );
  const revenuePerAction =
    effectiveOutputCount * outputSellPriceAfterTax -
    totalInputCount * inputBuyPrice;

  const revenuePer10Seconds =
    (revenuePerAction / effectiveActionTimeSeconds) * 10;
  // Buffs last 300 seconds. Cost is spread over that duration (300s / 10s = 30).
  const buffCostPer10Seconds =
    [
      buffOptions.useCraftingTea ? buffOptions.craftingTeaPrice : 0,
      buffOptions.useSuperCraftingTea ? buffOptions.superCraftingTeaPrice : 0,
      buffOptions.useUltraCraftingTea ? buffOptions.ultraCraftingTeaPrice : 0,

      buffOptions.useArtisanTea ? buffOptions.artisanTeaPrice : 0,
      buffOptions.useEfficiencyTea ? buffOptions.efficiencyTeaPrice : 0,
    ].reduce((sum, cost) => sum + cost, 0) / 30;

  const branchOfInsightProfitPer10s =
    ((branchOfInsight.dropChance * 1) / effectiveActionTimeSeconds) * 10;
  const branchOfInsightSellPriceAfterTax = applyMarketplaceTax(
    branchOfInsight.marketSell,
    marketplaceTaxPercent,
  );
  const branchOfInsightProfit: number =
    branchOfInsightProfitPer10s * branchOfInsightSellPriceAfterTax;

  return (
    revenuePer10Seconds -
    buffCostPer10Seconds +
    branchOfInsightProfit * Number(branchOfInsight.includeInCalculation)
  );
}

export default function CraftingPage() {
  const [filter, setFilter] = useState("");
  const [entries, setEntries] = useState<CraftingEntry[]>([]);
  const [branchOfInsightSellPrice, setBranchOfInsightSellPrice] = useState(0);
  const [rareFindModifier, setRareFindModifier] = useState(0);
  const [marketplaceTaxPercent, setMarketplaceTaxPercent] = useState(
    DEFAULT_MARKETPLACE_TAX_PERCENT,
  );

  const [useCraftingTea, setUseCraftingTea] = useState(false);
  const [useSuperCraftingTea, setUseSuperCraftingTea] = useState(false);
  const [useUltraCraftingTea, setUseUltraCraftingTea] = useState(false);

  const [useArtisanTea, setUseArtisanTea] = useState(false);
  const [useEfficiencyTea, setUseEfficiencyTea] = useState(false);
  const [useBranchOfInsightInProfits, setUseBranchOfInsightInProfits] =
    useState(false);

  const [buffMarketData, setBuffMarketData] =
    useState<CraftingBuffsMarketData | null>(null);

  async function loadBuffSellPrices() {
    const res = await fetch("/api/market/crafting_buffs");
    if (!res.ok) return;
    const data: CraftingBuffsMarketData = await res.json();
    setBuffMarketData(data);
  }

  async function loadEntries() {
    const res = await fetch("/api/crafting/profit");

    if (!res.ok) {
      setEntries([]);
      return;
    }

    const data = await res.json();

    const mapped: CraftingEntry[] = data.actions.map((action: any) => ({
      name: action.action,
      outputCountMin: action.outputCountMin,
      outputCountMax: action.outputCountMax,

      inputItemId: action.inputItemId,
      inputCount: action.inputCount,

      actionTimeSeconds: action.actionTimeSeconds,
      outputSellPrice: action.outputSellPrice,
      inputBuyPrice: action.inputBuyPrice,
      branchOfInsightDropChance: action.branchOfInsightDropChance,
    }));

    setEntries(mapped);
    setBranchOfInsightSellPrice(data.branchOfInsightSellPrice);
  }

  useEffect(() => {
    loadEntries();
    loadBuffSellPrices();
  }, []);

  const buffOptions: CraftingBuffOptions = {
    useCraftingTea,
    craftingTeaPrice: buffMarketData?.craftingTeaSell ?? NaN,
    useSuperCraftingTea,
    superCraftingTeaPrice: buffMarketData?.superCraftingTeaSell ?? NaN,
    useUltraCraftingTea,
    ultraCraftingTeaPrice: buffMarketData?.ultraCraftingTeaSell ?? NaN,

    useArtisanTea,
    artisanTeaPrice: buffMarketData?.artisanTeaSell ?? NaN,
    useEfficiencyTea,
    efficiencyTeaPrice: buffMarketData?.efficiencyTeaSell ?? NaN,
  };

  const filteredEntries = useMemo(() => {
    const withProfit = entries
      .map((entry) => ({
        ...entry,
        profitPer10s: computeProfitPer10s(
          entry,
          entry.inputBuyPrice,
          entry.outputSellPrice,
          buffOptions,
          {
            dropChance: computeEffectiveDropChance(entry),
            marketSell: branchOfInsightSellPrice,
            includeInCalculation: useBranchOfInsightInProfits,
          },
          marketplaceTaxPercent,
        ),
      }))
      .sort((a, b) => b.profitPer10s - a.profitPer10s);

    if (filter.trim() === "") return withProfit;

    return withProfit.filter((entry) =>
      entry.name.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [
    entries,
    filter,
    useCraftingTea,
    useSuperCraftingTea,
    useUltraCraftingTea,

    useArtisanTea,
    useEfficiencyTea,
    buffMarketData,
    useBranchOfInsightInProfits,
    marketplaceTaxPercent,
  ]);

  function computeEffectiveDropChance(entry: CraftingEntry): number {
    return entry.branchOfInsightDropChance * (1 + rareFindModifier / 100);
  }

  return (
    <div className="mt-4 px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Crafting Profit Calculator"
        description="This list shows the potential profits for each lumber action."
      />

      {/* MARKETPLACE */}
      <div className="mt-8 space-y-3">
        <h2>Marketplace</h2>
        <MarketplaceTaxInput
          value={marketplaceTaxPercent}
          onChange={setMarketplaceTaxPercent}
        />
      </div>

      {/* BUFFS */}
      <div className="mt-8 space-y-3">
        <h2>Buffs</h2>
        <AppCheckbox
          id="crafting_tea"
          label="Crafting Tea"
          description="2% Efficiency bonus and increases Crafting level by 3 for 300 seconds"
          checked={useCraftingTea}
          onChange={setUseCraftingTea}
        />
        <AppCheckbox
          id="super_crafting_tea"
          label="Super Crafting Tea"
          description="4% Efficiency bonus and increases Crafting level by 6 for 300 seconds"
          checked={useSuperCraftingTea}
          onChange={setUseSuperCraftingTea}
        />
        <AppCheckbox
          id="ultra_crafting_tea"
          label="Ultra Crafting Tea"
          description="6% Efficiency bonus and increases Crafting level by 8 for 300 seconds"
          checked={useUltraCraftingTea}
          onChange={setUseUltraCraftingTea}
        />
        <AppCheckbox
          id="artisan_tea"
          label="Artisan Tea"
          description="Reduces required material during production by 10%"
          checked={useArtisanTea}
          onChange={setUseArtisanTea}
        />
        <AppCheckbox
          id="efficiency_tea"
          label="Efficiency Tea"
          description="10% Efficiency bonus for 300 seconds"
          checked={useEfficiencyTea}
          onChange={setUseEfficiencyTea}
        />
        <h2>Rare drops</h2>
        <AppCheckbox
          id="include_branch_of_insight_profit"
          label="Include Branch Of Insight drop in profits."
          description={
            <span>
              Market sell value are <Coins value={branchOfInsightSellPrice} />{" "}
              coins.
            </span>
          }
          checked={useBranchOfInsightInProfits}
          onChange={setUseBranchOfInsightInProfits}
        />
      </div>

      {/* FILTER */}
      <FilterBar value={filter} onChange={setFilter} showSortButton={false} />

      {/* TABLE */}
      <ProfitTable
        columns={[
          "Tree",
          "Profit per 10s",
          ...(useBranchOfInsightInProfits
            ? ["Branch of Insight Drop Chance"]
            : []),
          "Sell value",
          "Action Time",
        ]}
      >
        {filteredEntries.map((entry) => (
          <tr key={entry.name}>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
              {entry.name}
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.profitPer10s} />
            </td>
            {useBranchOfInsightInProfits && (
              <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                {computeEffectiveDropChance(entry).toFixed(4)}%
              </td>
            )}
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.outputSellPrice} />
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.actionTimeSeconds} />s
            </td>
          </tr>
        ))}
      </ProfitTable>
    </div>
  );
}
